import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  const token = searchParams.get('token');

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        await verifyEmail(token);
        setStatus('success');
        toast.success('Email verified successfully!');
      } catch (error) {
        setStatus('error');
        toast.error('Verification failed. The link may be invalid or expired.');
      }
    };

    verify();
  }, [token, verifyEmail]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-8">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img 
              src="/ors-logo.svg" 
              alt="Onsite Rail Solutions" 
              className="h-12 w-auto"
              srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
              data-testid="verify-email-logo"
            />
          </div>

          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 bg-[#005EF8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-[#005EF8] animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-[#323033] mb-2">Verifying your email...</h1>
              <p className="text-[#5A5A5A]">Please wait while we verify your email address.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#323033] mb-2">Email verified!</h1>
              <p className="text-[#5A5A5A]">
                Your email has been successfully verified. You can now log in to your account.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#323033] mb-2">Verification failed</h1>
              <p className="text-[#5A5A5A]">
                The verification link is invalid or has expired. Please request a new verification email.
              </p>
            </>
          )}
        </CardHeader>

        <CardContent className="pt-4">
          {status === 'success' && (
            <Link to="/login">
              <Button className="w-full bg-[#005EF8] hover:bg-[#0047C2] text-white h-11" data-testid="go-to-login-btn">
                Go to Login
              </Button>
            </Link>
          )}

          {status === 'error' && (
            <Link to="/login">
              <Button variant="outline" className="w-full" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
